import { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './changeLanguage.module.scss'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonText from '@/components/_custom/Buttons/ButtonText/ButtonText'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { useMutation, useQuery } from '@apollo/client'
import { UPDATE_ACCOUNT_LANGUAGE } from '@/modules/API/account/accountHandler'
import { Language } from '@/types/Language'
import Loading from '@/components/Loading/Loading'
import { GET_LANGUAGES } from '@/modules/API/software/language'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { AutocompleteRenderInputParams } from '@mui/material'

interface ChangeLanguageDialogProps {
    open: boolean
    handleClose: () => void
    parent?: boolean
    signUp?: boolean
}

const ChangeLanguageDialog: FunctionComponent<ChangeLanguageDialogProps> = ({
    open,
    handleClose,
    parent = false,
    signUp = false
}: ChangeLanguageDialogProps) => {
    const { t, i18n } = useTranslation()
    const [language, setLanguage] = useState<string>(i18n.language)

    const [updateAccountLanguage] = useMutation(UPDATE_ACCOUNT_LANGUAGE, {
        variables: {
            language: language
        }
    })

    async function changeLanguage(selectedLanguage: string): Promise<void> {
        setLanguage(selectedLanguage)
        await i18n.changeLanguage(selectedLanguage)
        await updateAccountLanguage({ variables: { language: selectedLanguage } })
    }

    const [languages, setLanguages] = useState<Language[]>([])

    const { data: dataLanguages, loading: loadingLanguages, error: errorLanguages } = useQuery(GET_LANGUAGES)

    useEffect(() => {
        if (dataLanguages) {
            setLanguages(dataLanguages.getLanguages)
        }
    }, [dataLanguages])

    const [sortedLanguages, setSortedLanguages] = useState<Language[]>([])

    useEffect(() => {
        const firstLanguages: Language[] = languages.filter((l: Language) =>
            FIVE_FIRST_LANGUAGES.find((f: { id: string; place: number }) => f.id === l.id)
        )

        const otherLanguages: Language[] = languages.filter(
            (l: Language) => !FIVE_FIRST_LANGUAGES.find((f: { id: string; place: number }) => f.id === l.id)
        )

        setSortedLanguages([
            ...firstLanguages.sort((a: Language, b: Language) => {
                const aPlace: number =
                    FIVE_FIRST_LANGUAGES.find((f: { id: string; place: number }): boolean => f.id === a.id)?.place || 0
                const bPlace: number =
                    FIVE_FIRST_LANGUAGES.find((f: { id: string; place: number }): boolean => f.id === b.id)?.place || 0
                return aPlace - bPlace
            }),
            ...otherLanguages.sort((a: Language, b: Language) => a.name.localeCompare(b.name))
        ])
    }, [languages])

    const FIVE_FIRST_LANGUAGES: {
        id: string
        place: number
    }[] = [
        { id: 'fr', place: 1 },
        { id: 'ar', place: 2 },
        { id: 'en', place: 3 },
        { id: 'es', place: 4 },
        { id: 'de', place: 5 }
    ]

    function handleChange(_e: SyntheticEvent, value: string | null): void {
        const selectedLanguage: string | undefined = languages.find((l: Language) => l.name === value)?.id

        if (selectedLanguage) changeLanguage(selectedLanguage)
    }

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('chooseLanguage')} parent={parent} />
            <DialogBody className={styles.DialogBody}>
                {loadingLanguages && <Loading overall />}
                {errorLanguages && <ErrorMessage error={errorLanguages.message} />}
                {dataLanguages && (
                    <div className={styles.AutocompleteContainer}>
                        <Autocomplete
                            value={languages.find((l: Language) => l.id === language)?.name || language}
                            onChange={handleChange}
                            options={sortedLanguages.map((l: Language) => l.name)}
                            renderInput={(params: AutocompleteRenderInputParams) => (
                                <TextField {...params} label={t('language')} className={styles.Autocomplete} />
                            )}
                            className={`${styles.Autocomplete} 
                            ${parent ? styles.AutocompleteParent : styles.AutocompleteDaycare}`}
                        />
                    </div>
                )}
                <div className={styles.ButtonContainer}>
                    <ButtonText
                        text={t(signUp ? 'next' : 'close')}
                        onClick={handleClose}
                        parent={parent}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default ChangeLanguageDialog
